import React from 'react';
import { SquarePen, X, ZoomIn } from 'lucide-react';
import QuantityInput from '../../QuantityInput';
import Button from '../../Button';
import Price from '../../Price';
import './SimpleProduct.css';

export default function SimpleProduct({
  image,
  imageAlt = '',
  title,
  price,
  unitPrice,
  priceState = 'default',
  originalPrice = '',
  unitOriginalPrice,
  options = [],
  quantity,
  onQuantityChange,
  onEdit,
  onRemove,
  lockQuantity = false,
  showQuantity = true,         // false: a one-of line (no stepper; price = unitPrice) — each copy is its own line
  className = '',
  priceSuffix = ',-',
  imageFit = 'cover',          // 'cover' (product photo) | 'contain' (cut-out artwork shown whole)
  imageBackground,             // CSS background of the image well, e.g. a card surface for cut-outs
  variant = 'default',         // 'default' (thumbnail beside the text) | 'highlighted' (the artwork full width on top — a line's main product)
  onZoom,                      // highlighted only: the zoom button in the well's top-right corner
  zoomLabel = 'Zoom',
  children,                    // extra content under the options, e.g. a size choice the line still needs
}) {
  const highlighted = variant === 'highlighted';
  const qtyProps = quantity !== undefined
    ? { value: quantity }
    : { defaultValue: 1 };

  const effectiveQty = showQuantity && quantity !== undefined ? quantity : 1;
  const displayPrice = unitPrice != null
    ? `${unitPrice * effectiveQty}${priceSuffix}`
    : price;
  const displayOriginalPrice = unitOriginalPrice != null
    ? `${unitOriginalPrice * effectiveQty}${priceSuffix}`
    : originalPrice;

  return (
    <div className={`sc-simple-product${highlighted ? ' sc-simple-product--highlighted' : ''}${className ? ` ${className}` : ''}`}>
      <div className="sc-simple-product__row">
        <div className="sc-simple-product__image" style={imageBackground ? { '--sc-simple-product-image-bg': imageBackground } : undefined}>
          {image && (
            <img
              src={image}
              alt={imageAlt}
              className={`sc-simple-product__img${imageFit === 'contain' ? ' sc-simple-product__img--contain' : ''}`}
            />
          )}
          {highlighted && onZoom && (
            <Button
              size="tertiary"
              variant="text"
              iconOnly
              iconLeft={<ZoomIn size={20} strokeWidth={1.5} aria-hidden="true" />}
              onClick={onZoom}
              aria-label={zoomLabel}
              className="sc-simple-product__zoom"
            />
          )}
        </div>

        <div className="sc-simple-product__details">
          <div className="sc-simple-product__title-price">
            <p className="sc-simple-product__title typo-h4">{title}</p>
            <Price
              type="regular"
              state={priceState}
              price={displayPrice}
              originalPrice={displayOriginalPrice}
            />
          </div>
          {options.length > 0 && (
            <div className="sc-simple-product__options">
              {options.map((opt, idx) => (
                <div key={`${opt.label}-${idx}`} className="sc-simple-product__option">
                  <span className="sc-simple-product__option-label typo-body-sm">{opt.label}</span>
                  <span className="sc-simple-product__option-value typo-body">{opt.value}</span>
                </div>
              ))}
            </div>
          )}
          {children && <div className="sc-simple-product__extra">{children}</div>}
        </div>

        <div className={`sc-simple-product__actions${showQuantity ? '' : ' sc-simple-product__actions--no-quantity'}`}>
          {showQuantity && (
            <QuantityInput
              {...qtyProps}
              onChange={onQuantityChange}
              min={1}
              disabled={lockQuantity}
            />
          )}
          <div className="sc-simple-product__edit-remove">
            {onEdit && (
              <Button
                size="secondary"
                variant="text"
                iconOnly
                iconLeft={<SquarePen size={20} />}
                onClick={onEdit}
              />
            )}
            {onRemove && (
              <Button
                size="secondary"
                variant="text-danger"
                iconOnly
                iconLeft={<X size={20} />}
                onClick={onRemove}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
